








































































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import ClientPartnerPackageDialogComponent from "@/components/admin/clients/partners/ClientPartnerPackageDialog.vue";
import { ClientPartner, ClientPartnerPackage, ClientPartnerPackageList, PARTNER_PACKAGE_FILES, PartnerPackageFile } from "@/data/models/ClientPartner";
import { getClientPartnerPackageFileUrl, listClientPartnerPackages } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { packageOperationsText, packageRecipientText, packageSettingChangeText, packagesAfterCreating, partnerErrorKey } from "@/utils/ClientPartners";
import { COLOR_ALL_DEVICES_EDIT_ICON } from "@/utils/colors";
import { formatDateToDayMonthYear, formatDateToHoursAndMinuteString } from "@/utils/DateHelper";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";

const PACKAGES = "admin.clients.partners.packages";

/** Each file's type, for its icon: the description as YAML and as a page, the overview as PDF. */
const FILE_TYPES: Record<PartnerPackageFile, DownloadFileType> = {
  yaml: DownloadFileType.YAML,
  html: DownloadFileType.HTML,
  overview: DownloadFileType.PDF,
};

@Component({
  components: { "client-partner-package-dialog": ClientPartnerPackageDialogComponent },
})
export default class ClientPartnerPackages extends Vue {
  @Ref("packageDialog") readonly packageDialog!: ClientPartnerPackageDialogComponent;

  /** The system's owner, whose page this is. */
  @Prop({ required: true }) clientId!: string;
  @Prop({ required: true }) partner!: ClientPartner;
  @Prop({ default: false }) mayEdit!: boolean;

  list: ClientPartnerPackageList | null = null;
  isLoading = true;
  loadError = false;
  /** `packageId/file` while its link is fetched; null otherwise. */
  downloading: string | null = null;

  readonly files = PARTNER_PACKAGE_FILES;
  /** Row icons are primary blue (ui-conventions.md, "Accent or primary goes by where the control sits"). */
  readonly iconColor = COLOR_ALL_DEVICES_EDIT_ICON;

  fileIcon(file: PartnerPackageFile): string {
    return getFileTypeIcon(FILE_TYPES[file]);
  }

  dateText(item: ClientPartnerPackage): string {
    if (item.createdAtMillis === null) {
      return "–";
    }
    const date = new Date(item.createdAtMillis);
    return `${formatDateToDayMonthYear(date)} ${formatDateToHoursAndMinuteString(date)}`;
  }

  recipientText(item: ClientPartnerPackage): string {
    return this.$t(`${PACKAGES}.for`, [packageRecipientText(item.recipient)]).toString();
  }

  /** Outlined chips (ui-conventions.md): attention for out of date or not checkable, a result for current, grey for superseded. */
  stateColor(item: ClientPartnerPackage): string | undefined {
    return { CURRENT: "success", OUTDATED: "warning", UNCHECKED: "warning", SUPERSEDED: undefined }[item.state];
  }

  /**
   * Why the newest package is out of date, one line each: the endpoints, the description's general parts,
   * the settings, then what changed in the API since (English, as the partner's documents are).
   */
  reasons(item: ClientPartnerPackage): Array<{ text: string; className: string }> {
    const outdated = item.outdated;
    if (!outdated) {
      return [];
    }
    const t = (key: string, values?: unknown[]) => this.$t(`${PACKAGES}.${key}`, values).toString();
    const lines: Array<{ text: string; className: string }> = [];
    const line = (text: string, className = "") => lines.push({ text, className });
    if (item.state === "UNCHECKED") {
      line(t("unchecked"));
    }
    (["added", "removed", "changed"] as const)
      .filter((kind) => outdated.operations[kind].length > 0)
      .forEach((kind) => line(t(`operations.${kind}`, [packageOperationsText(outdated.operations[kind])])));
    if (outdated.general.length > 0) {
      line(t("general", [outdated.general.map((part) => t(`generalParts.${part}`)).join(", ")]));
    }
    outdated.settings.forEach((change) => line(packageSettingChangeText(change, (key) => this.$t(key).toString())));
    if (!outdated.settingsChecked && item.state !== "SUPERSEDED") {
      line(t("settingsNotChecked"), "grey--text");
    }
    outdated.changelog.forEach((version) =>
      version.changes.forEach((change) => line(t("changelogLine", [version.version, change]), "grey--text"))
    );
    return lines;
  }

  groupsText(item: ClientPartnerPackage): string {
    return item.groups.map((group) => this.$t(`${PACKAGES}.groupNames.${group}`).toString()).join(", ");
  }

  /** NEUES PAKET: a created package goes to the top of the list, which is not read again. */
  async onCreate() {
    if (!this.list) {
      return;
    }
    const created = await this.packageDialog.show(this.clientId, this.partner, this.list);
    if (created) {
      this.list = { ...this.list, packages: packagesAfterCreating(created, this.list.packages), startGroups: created.groups };
    }
  }

  /**
   * The link is short-lived, so it is fetched on the click. It downloads (IRES Cloud sends it as an
   * attachment), so the page stays and no tab is needed, as the report list's downloads.
   */
  async onDownload(item: ClientPartnerPackage, file: PartnerPackageFile) {
    this.downloading = `${item.id}/${file}`;
    try {
      window.location.assign(await getClientPartnerPackageFileUrl(this.clientId, item.id, file));
    } catch (error) {
      SnackbarModule.showError({ key: partnerErrorKey(error) });
    } finally {
      this.downloading = null;
    }
  }

  @Watch("partner.id")
  async load() {
    this.isLoading = true;
    this.loadError = false;
    try {
      this.list = await listClientPartnerPackages(this.clientId, this.partner.id);
    } catch {
      // Shown in the card; the rest of the page stands.
      this.loadError = true;
    }
    this.isLoading = false;
  }

  mounted() {
    this.load();
  }
}
